import React, { useState, useEffect, useCallback } from 'react'
import apiClient, { BASE_URL, API_ENDPOINTS } from '../../config/api'
import Swal from 'sweetalert2'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const ManageUsers = () => {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [branches, setBranches] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  })

  const applyFilters = useCallback(() => {
    let filtered = [...users]

    // Search filter (name, email, code)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(user =>
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.code?.toLowerCase().includes(searchLower) ||
        user.user?.email?.toLowerCase().includes(searchLower)
      )
    }

    // Role filter
    if (filters.role) {
      filtered = filtered.filter(user => user.user?.role === filters.role)
    }

    // Status filter
    if (filters.status !== '') {
      const isActive = filters.status === 'active'
      filtered = filtered.filter(user => user.user?.is_active === isActive)
    }

    setFilteredUsers(filtered)
  }, [users, filters])

  const {
    paginatedItems: paginatedUsers,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredUsers)

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(`${BASE_URL}/api/accounts/users/`)
      setUsers(response.data)
      setFilteredUsers(response.data)
    } catch (error) {
      console.error('Error fetching users:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch users. Please try again.',
        confirmButtonColor: '#d33'
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchBranches = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
      const branchesData = response.data.results || response.data || []
      setBranches(branchesData)
      return branchesData
    } catch (error) {
      console.error('Error fetching branches:', error)
      setBranches([])
      return []
    }
  }

  const fetchWarehouses = async () => {
    try {
      // Fetch all warehouses without any filtering - same as ManageWarehouses.js
      const response = await apiClient.get(API_ENDPOINTS.WAREHOUSES)
      const warehousesData = response.data.results || response.data || []

      // Enhanced debug logging
      console.log('=== WAREHOUSE FETCH DEBUG ===')
      console.log('Raw response:', response.data)
      console.log('Warehouses array:', warehousesData)
      console.log('Total warehouses:', warehousesData.length)
      console.log('Main warehouses:', warehousesData.filter(w => w.is_main))
      console.log('Non-main warehouses:', warehousesData.filter(w => !w.is_main))
      console.log('===========================')

      setWarehouses(warehousesData)
      return warehousesData
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      setWarehouses([])
      return []
    }
  }

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = !currentStatus
      await apiClient.patch(`${BASE_URL}/api/accounts/users/update-status/`, {
        user_id: userId
      })

      // Update the user in the state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.user?.id === userId
            ? { ...user, user: { ...user.user, is_active: newStatus } }
            : user
        )
      )

      // Update filtered users as well
      setFilteredUsers(prevFiltered =>
        prevFiltered.map(user =>
          user.user?.id === userId
            ? { ...user, user: { ...user.user, is_active: newStatus } }
            : user
        )
      )

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `User has been ${newStatus ? 'activated' : 'blocked'} successfully`,
        confirmButtonColor: '#3085d6',
        timer: 2000,
        showConfirmButton: true
      })
    } catch (error) {
      console.error('Error updating user status:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.response?.data?.error || 'Failed to update user status. Please try again.',
        confirmButtonColor: '#d33'
      })
    }
  }

  const updateUser = async (user, formData) => {
    try {
      const userId = user.id  // Employee pk — backend does Employee.objects.get(pk=pk)

      // Build the payload using Employee model field names as expected by EmployeeSerializer
      const branchId = formData.branch_id ? parseInt(formData.branch_id, 10) : null
      const warehouseId = formData.warehouse_id ? parseInt(formData.warehouse_id, 10) : null

      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
      }

      // Assign branch or warehouse based on role; null-out the other
      if (formData.role === 'branch_manager' || formData.role === 'cashier') {
        payload.branch = branchId
        payload.warehouse = null
      } else if (formData.role === 'warehouse_manager') {
        payload.warehouse = warehouseId
        payload.branch = null
      } else {
        payload.branch = null
        payload.warehouse = null
      }

      // PUT /api/accounts/user/<pk>/ — UserDetailView with EmployeeSerializer (partial=True)
      const response = await apiClient.put(`${BASE_URL}/api/accounts/user/${userId}/`, payload)
      console.log('User update response:', response)

      await fetchUsers()

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'User updated successfully',
        confirmButtonColor: '#3085d6',
        timer: 2000,
        showConfirmButton: true
      })
    } catch (error) {
      console.error('Error updating user:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to update user. Please try again.',
        confirmButtonColor: '#d33'
      })
      throw error
    }
  }

  const editUser = async (user) => {
    // Fetch branches and warehouses if needed
    const [branchesData, warehousesData] = await Promise.all([fetchBranches(), fetchWarehouses()])

    const currentRole = user.user?.role
    const showBranch = currentRole === 'branch_manager' || currentRole === 'cashier'
    const showWarehouse = currentRole === 'warehouse_manager'

    const editFormHtml = `
      <form id="editUserForm" style="text-align: left; max-width: 100%; box-sizing: border-box; overflow: hidden;">
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">First Name <span style="color: red;">*</span></label>
          <input type="text" id="edit_first_name" class="swal2-input" value="${user.first_name || ''}" required style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px;">
        </div>
        <div style="margin-bottom: 12px;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Last Name <span style="color: red;">*</span></label>
          <input type="text" id="edit_last_name" class="swal2-input" value="${user.last_name || ''}" required style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; font-size: 14px;">
        </div>
        <div id="edit_branch_container" style="margin-bottom: 12px; ${showBranch ? '' : 'display: none;'}">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Branch <span style="color: red;">*</span></label>
          <select id="edit_branch_id" class="swal2-select" style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;">
            <option value="">Select Branch</option>
            ${branchesData.map(branch => `<option value="${branch.id}" ${user.branch === branch.id ? 'selected' : ''}>${branch.name || `Branch ${branch.id}`}</option>`).join('')}
          </select>
        </div>
        <div id="edit_warehouse_container" style="margin-bottom: 12px; ${showWarehouse ? '' : 'display: none;'}">
          <label style="display: block; margin-bottom: 4px; font-weight: 500; color: #2c3e50; font-size: 13px;">Warehouse <span style="color: red;">*</span></label>
          <select id="edit_warehouse_id" class="swal2-select" style="width: 100%; max-width: 100%; box-sizing: border-box; padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;">
            <option value="">Select Warehouse</option>
            ${warehousesData.map(warehouse => `<option value="${warehouse.id}" ${user.warehouse === warehouse.id ? 'selected' : ''}>${warehouse.name || `Warehouse ${warehouse.id}`}</option>`).join('')}
          </select>
        </div>
      </form>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 16px; font-weight: 600;">Edit User</span>',
      html: editFormHtml,
      width: '450px',
      padding: '1.5rem',
      showCancelButton: true,
      confirmButtonText: 'Update User',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal2-popup-edit',
        htmlContainer: 'swal2-html-container-edit'
      },
      didOpen: () => {
        const popup = document.querySelector('.swal2-popup')
        if (popup) {
          popup.style.maxWidth = '450px'
          popup.style.overflow = 'hidden'
          popup.style.boxSizing = 'border-box'
        }
        const htmlContainer = document.querySelector('.swal2-html-container')
        if (htmlContainer) {
          htmlContainer.style.maxWidth = '100%'
          htmlContainer.style.overflow = 'hidden'
          htmlContainer.style.boxSizing = 'border-box'
          htmlContainer.style.padding = '0'
        }
        const form = document.getElementById('editUserForm')
        if (form) {
          form.style.maxWidth = '100%'
          form.style.overflow = 'hidden'
        }
        document.querySelectorAll('#editUserForm input, #editUserForm select').forEach(el => {
          el.style.maxWidth = '100%'
          el.style.boxSizing = 'border-box'
        })
      },
      preConfirm: async () => {
        const formData = {
          first_name: document.getElementById('edit_first_name').value.trim(),
          last_name: document.getElementById('edit_last_name').value.trim(),
          role: currentRole, // role is read-only here; passed through for branch/warehouse logic
          branch_id: document.getElementById('edit_branch_id')?.value || '',
          warehouse_id: document.getElementById('edit_warehouse_id')?.value || '',
        }

        if (!formData.first_name || !formData.last_name) {
          Swal.showValidationMessage('First name and last name are required')
          return false
        }

        if ((currentRole === 'branch_manager' || currentRole === 'cashier') && !formData.branch_id) {
          Swal.showValidationMessage('Branch is required for this role')
          return false
        }

        if (currentRole === 'warehouse_manager' && !formData.warehouse_id) {
          Swal.showValidationMessage('Warehouse is required for this role')
          return false
        }

        return formData
      }
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          await updateUser(user, result.value)
        } catch (error) {
          // Error already handled in updateUser
        }
      }
    })
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    })
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      role: '',
      status: ''
    })
  }

  const getRoleBadgeClass = (role) => {
    const roleClasses = {
      'admin': 'bg-danger',
      'owner': 'bg-primary',
      'branch_manager': 'bg-success',
      'warehouse_manager': 'bg-info',
      'cashier': 'bg-warning',
      'auditor': 'bg-secondary'
    }
    return roleClasses[role] || 'bg-dark'
  }

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge bg-success">Active</span>
    ) : (
      <span className="badge bg-secondary">Inactive</span>
    )
  }

  const showUserDetails = (user) => {
    const isActive = user.user?.is_active
    const statusColor = isActive ? '#28a745' : '#6c757d'
    const statusText = isActive ? 'Active' : 'Blocked'
    const statusIcon = isActive ? '✓' : '✗'

    const detailsHtml = `
      <div style="text-align: left; padding: 10px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 8px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 2px solid rgba(0,0,0,0.1);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: bold; margin-right: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
            ${user.first_name?.[0]?.toUpperCase() || ''}${user.last_name?.[0]?.toUpperCase() || ''}
          </div>
          <div>
            <h3 style="margin: 0; color: #2c3e50; font-weight: 600; font-size: 16px;">${user.first_name || ''} ${user.last_name || ''}</h3>
            <p style="margin: 2px 0 0 0; color: #7f8c8d; font-size: 12px;">${user.code || 'N/A'}</p>
          </div>
        </div>
        
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Email</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${user.user?.email || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Role</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${user.user?.role_display || user.user?.role || 'N/A'}</div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Status</div>
            <div style="color: ${statusColor}; font-weight: 600; font-size: 12px;">
              <span style="display: inline-block; width: 16px; height: 16px; background: ${statusColor}; border-radius: 50%; color: white; text-align: center; line-height: 16px; font-size: 10px; margin-right: 4px;">${statusIcon}</span>
              ${statusText}
            </div>
          </div>
          
          <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
            <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">Date Joined</div>
            <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">${user.user?.date_joined ? new Date(user.user.date_joined).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</div>
          </div>
        </div>
        
        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); margin-bottom: 8px;">
          <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 6px;">Location</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
            <div>
              <div style="color: #95a5a6; font-size: 9px; margin-bottom: 2px;">Branch</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 11px;">${user.branch_name || user.user?.branch_name || 'Not Assigned'}</div>
            </div>
            <div>
              <div style="color: #95a5a6; font-size: 9px; margin-bottom: 2px;">Warehouse</div>
              <div style="color: #2c3e50; font-weight: 500; font-size: 11px;">${user.warehouse_name || user.user?.warehouse_name || 'Not Assigned'}</div>
            </div>
          </div>
        </div>
        
        <div style="background: white; padding: 8px; border-radius: 6px; box-shadow: 0 1px 4px rgba(0,0,0,0.1);">
          <div style="color: #7f8c8d; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px;">First Login</div>
          <div style="color: #2c3e50; font-weight: 500; font-size: 12px;">
            ${user.user?.first_login ? '<span style="color: #e74c3c;">Pending</span>' : '<span style="color: #27ae60;">Completed</span>'}
          </div>
        </div>
      </div>
    `

    Swal.fire({
      title: '<span style="color: #2c3e50; font-size: 18px; font-weight: 600;">User Details</span>',
      html: detailsHtml,
      icon: 'info',
      showCancelButton: true,
      showDenyButton: true,
      showCloseButton: true,
      confirmButtonText: '<i class="bi bi-pencil"></i> Edit User',
      denyButtonText: '<i class="bi bi-trash"></i> Delete User',
      cancelButtonText: isActive ? '<i class="bi bi-lock-fill"></i> Block User' : '<i class="bi bi-unlock-fill"></i> Activate User',
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#d33',
      cancelButtonColor: isActive ? '#dc3545' : '#28a745',
      width: '550px',
      heightAuto: false,
      padding: '1rem',
      customClass: {
        popup: 'swal2-popup-custom'
      },
      footer: '<button id="swal-close-btn" class="btn btn-secondary" style="margin-top: 10px; padding: 8px 16px; border: none; border-radius: 5px;">Close</button>',
      didOpen: () => {
        const confirmButton = Swal.getConfirmButton()
        const denyButton = Swal.getDenyButton()
        const cancelButton = Swal.getCancelButton()
        const closeButton = document.getElementById('swal-close-btn')

        if (confirmButton) {
          confirmButton.style.border = 'none'
          confirmButton.style.padding = '10px 20px'
          confirmButton.style.borderRadius = '5px'
          confirmButton.style.fontWeight = '500'
          confirmButton.style.marginRight = '10px'
        }

        if (denyButton) {
          denyButton.style.border = 'none'
          denyButton.style.padding = '10px 20px'
          denyButton.style.borderRadius = '5px'
          denyButton.style.fontWeight = '500'
          denyButton.style.marginRight = '10px'
        }

        if (cancelButton) {
          cancelButton.style.border = 'none'
          cancelButton.style.padding = '10px 20px'
          cancelButton.style.borderRadius = '5px'
          cancelButton.style.fontWeight = '500'
          cancelButton.style.marginRight = '10px'
        }

        if (closeButton) {
          closeButton.onclick = () => Swal.close()
        }
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Edit user
        await editUser(user)
      } else if (result.isDenied) {
        // Delete user
        await handleDelete(user.user?.id)
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        // Activate/Deactivate user
        await handleToggleStatus(user.user?.id, isActive)
      }
    })
  }

  const handleDelete = async (userId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    })

    if (result.isConfirmed) {
      try {
        const response = await apiClient.delete(`${BASE_URL}/api/accounts/users/`, {
          data: { user_id: userId }
        })
        console.log('Delete user response:', response?.data || response)

        // Remove user from state
        setUsers(prevUsers => prevUsers.filter(user => user.user?.id !== userId))
        setFilteredUsers(prevFiltered => prevFiltered.filter(user => user.user?.id !== userId))

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'User has been deleted successfully.',
          confirmButtonColor: '#3085d6',
          timer: 2000,
          showConfirmButton: true
        })
      } catch (error) {
        console.error('Error deleting user:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || error.response?.data?.error || 'Failed to delete user. Please try again.',
          confirmButtonColor: '#d33'
        })
      }
    }
  }

  // Get unique roles for filter dropdown
  const uniqueRoles = [...new Set(users.map(user => user.user?.role).filter(Boolean))]

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Users</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Home</li>
            <li className="breadcrumb-item">Users</li>
            <li className="breadcrumb-item active">Manage Users</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    <i className="bi bi-people me-2"></i>
                    Manage Users
                  </h5>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={fetchUsers}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </button>
                </div>
                <p className="text-muted">View and manage all system users, their roles, and permissions.</p>

                {/* Filters Section */}
                <div className="card mb-3">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="bi bi-funnel me-2"></i>
                      Filters
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label htmlFor="search" className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          id="search"
                          name="search"
                          placeholder="Name, Email, or Code"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="role" className="form-label">Role</label>
                        <select
                          className="form-select"
                          id="role"
                          name="role"
                          value={filters.role}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Roles</option>
                          {uniqueRoles.map(role => (
                            <option key={role} value={role}>
                              {users.find(u => u.user?.role === role)?.user?.role_display || role}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <select
                          className="form-select"
                          id="status"
                          name="status"
                          value={filters.status}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="col-md-2 d-flex align-items-end">
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={clearFilters}
                          title="Clear filters"
                        >
                          <i className="bi bi-x-lg me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users Table */}
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading users...</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                    <p className="mt-3 text-muted">No users found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Warehouse</th>
                          <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                                  {user.first_name?.[0]?.toUpperCase()}{user.last_name?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <div className="fw-semibold">{user.first_name} {user.last_name}</div>
                                  <small className="text-muted">{user.code}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getRoleBadgeClass(user.user?.role)}`}>
                                {user.user?.role_display || user.user?.role || 'N/A'}
                              </span>
                            </td>
                            <td>
                              {getStatusBadge(user.user?.is_active)}
                            </td>
                            <td>
                              {user.warehouse_name || user.user?.warehouse_name || (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => showUserDetails(user)}
                                title="View Details"
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      startIndex={startIndex}
                      endIndex={endIndex}
                      pageSize={pageSize}
                      onPageChange={setCurrentPage}
                      onPageSizeChange={setPageSize}
                      itemLabel="users"
                    />
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageUsers

